/* Session Storage Manager for step information */
export class StepInfoManager {
  static STORAGE_KEY = 'step-info';

  /**
   * Gets current step info from session storage
   * @returns {Object|null} The step info object or null if not found
   */
  static getStepInfo() {
    try {
      const stepInfo = sessionStorage.getItem(this.STORAGE_KEY);
      const parsedStepInfo = stepInfo ? JSON.parse(stepInfo) : null;
      
      return parsedStepInfo;
    } catch (error) {
      console.error('Error retrieving step-info from session storage:', error);
      return null;
    }
  }

  /**
   * Updates step info in session storage
   * @param {Object} updates - Object with properties to update
   * @returns {Object} Updated step info
   */
  static updateStepInfo(updates) {
    try {
      const currentStepInfo = this.getStepInfo() || {};
      const updatedStepInfo = { ...currentStepInfo, ...updates };

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedStepInfo));

      return updatedStepInfo;
    } catch (error) {
      console.error('Error updating step-info in session storage:', error);
      return this.getStepInfo() || {};
    }
  }

  /**
   * Initializes step info with default values
   * @param {string} collectionHandle - The collection handle
   * @param {number} totalSteps - Total number of steps
   * @param {number} currentStep - Current step (default: 1)
   * @returns {Object} Initialized step info
   */
  static defaultStepInfo(collectionHandle, totalSteps, currentStep = 1) {
    try {
      const initialStepInfo = {
        'collection-handle': collectionHandle,
        'step-count': currentStep,
        'total-steps': totalSteps
      };

      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(initialStepInfo));

      return initialStepInfo;
    } catch(error) {
      console.error('Error initializing step info:', error);
      return this.getStepInfo() || {};
    }
  }

  /**
   * Creates flow-state-changed event
   * @param {Object} stepInfo - The step info to include in event detail
   * @returns {CustomEvent} The flow-state-changed event
   */
  static createFlowStateChangedEvent(stepInfo) {
    return new CustomEvent('flow-state-changed', {
      detail: { stepInfo },
      bubbles: true,
      composed: true
    });
  }

  /**
   * Creates flow-visual-update-requested event
   * @param {Object} stepInfo - The step info to include in event detail
   * @returns {CustomEvent} The flow-visual-update-requested event
   */
  static createFlowVisualUpdateRequestedEvent(stepInfo) {
    return new CustomEvent('flow-visual-update-requested', {
      detail: { stepInfo },
      bubbles: true,
      composed: true
    });
  }

  /**
   * Dispatches flow-state-changed event to document
   * @param {Object} stepInfo - The step info to include in event detail
   */
  static dispatchFlowStateChanged(stepInfo) {
    try {
      document.dispatchEvent(this.createFlowStateChangedEvent(stepInfo));
    } catch (error) {
      console.error('Error dispatching flow state changed event:', error);
      throw error;
    }
  }

  /**
   * Dispatches flow-visual-update-requested event to document
   * @param {Object} stepInfo - The step info to include in event detail
   */
  static dispatchFlowVisualUpdateRequested(stepInfo) {
    try {
      document.dispatchEvent(this.createFlowVisualUpdateRequestedEvent(stepInfo));
    } catch (error) {
      console.error('Error dispatching flow visual update requested event:', error);
      throw error;
    }
  }
}

export class FilterUpdateManager {
  /**
   * Finds matching product type filter for given step type
   * @param {string} stepType - The step type to match
   * @param {NodeListOf<Element>} productTypeInputs - Product type input elements
   * @returns {Object|null} Matching filter object or null
   */
  static findMatchingProductTypeFilter(stepType, productTypeInputs) {
    try {
      let matchingProductTypeFilter = null;
      const normalizedStepType = stepType.toLowerCase().replace(/[^a-z0-9]/g, '');
      
      productTypeInputs.forEach((input, index) => {
        if (input instanceof HTMLInputElement) {
          const inputValue = input.value;
          const inputName = input.name;
          const normalizedInput = inputValue.toLowerCase().replace(/[^a-z0-9]/g, '');

          // Check if the input value matches the step type (bidirectional matching)
          if (normalizedInput.includes(normalizedStepType) || normalizedStepType.includes(normalizedInput)) {
            matchingProductTypeFilter = { name: inputName, value: inputValue };
          }
        }
      });

      if (!matchingProductTypeFilter) {
        console.warn('No matching product type filter found');
      }

      return matchingProductTypeFilter;
    } catch (error) {
      console.error('Error finding matching product type filter:', error);
      throw error;
    }
  }

  /**
   * Fallback method for applying filters manually
   * @param {HTMLElement} facetsForm - The facets form component
   * @param {Object|null} matchingProductTypeFilter - The matching product type filter
   * @returns {Promise<boolean>} - True if successful
   */
  static async applyFiltersFallback(facetsForm, matchingProductTypeFilter) {
    try {
      // Clear all non-inventory filters first
      const allInputs = facetsForm.querySelectorAll('input[type="checkbox"]');
      allInputs.forEach(input => {
        if (input instanceof HTMLInputElement && !input.name.includes('inventory_levels')) {
          input.checked = false;
        }
      });

      // Set the matching product type filter
      if (matchingProductTypeFilter) {
        const filter = /** @type {any} */ (matchingProductTypeFilter);
        const targetInput = facetsForm.querySelector(`input[name="${filter.name}"][value="${filter.value}"]`);
        if (targetInput instanceof HTMLInputElement) {
          targetInput.checked = true;
        }
      }

      // Try to update filters one more time
      if (typeof /** @type {any} */ (facetsForm).updateFilters === 'function') {
        await /** @type {any} */ (facetsForm).updateFilters();
        return true;
      } else {
        console.error('❌ [STEP-FILTER] All filter update methods failed');
        return false;
      }
    } catch (error) {
      console.error('Error applying filters fallback:', error);
      throw error;
    }
  }

  /**
   * Helper function to determine the best filter update method based on URL update preferences
   * @param {HTMLElement} facetsForm - The facets form component
   * @param {string} targetUrl - The target URL for filter updates
   * @returns {Promise<boolean>} - True if filter update was successful
   */
  static async updateFiltersWithBestMethod(facetsForm, targetUrl) {
    try {
      if (!facetsForm || !(facetsForm instanceof HTMLElement)) {
        return false;
      }

      // Check if URL updates should be disabled
      const disableURLUpdates = facetsForm.hasAttribute && facetsForm.hasAttribute('disable-url-updates') || 
        (/** @type {any} */ (facetsForm).disableURLUpdates !== undefined && /** @type {any} */ (facetsForm).disableURLUpdates);

      if (disableURLUpdates && typeof /** @type {any} */ (facetsForm).updateFiltersByURLWithoutBrowserUpdate === 'function') {
        // Use URL-less method if URL updates are disabled
        await /** @type {any} */ (facetsForm).updateFiltersByURLWithoutBrowserUpdate(targetUrl);
        
        return true;
      } else if (typeof /** @type {any} */ (facetsForm).updateFiltersByURL === 'function') {
        // Use normal URL update method
        await /** @type {any} */ (facetsForm).updateFiltersByURL(targetUrl);
        
        return true;
      } else if (typeof /** @type {any} */ (facetsForm).updateFilters === 'function') {
        // Fallback to updateFilters method
        await /** @type {any} */ (facetsForm).updateFilters();
        
        return true;
      } else {
        console.warn('⚠️ [FILTER-UPDATE] No suitable filter update method found');
        
        return false;
      }
    } catch (error) {
      console.error('Error updating filters with best method:', error);
      throw error;
    }
  }
}

export class LoadManager {
  static addLoader() {
    try {
      const body = document.body;

      if(body) {
        const loadContainer = document.createElement('div');
        loadContainer.className = 'load-container';
        loadContainer.id = 'load-container';
        
        const loadSpinner = document.createElement('div');
        loadSpinner.className = 'load';
        
        loadContainer.appendChild(loadSpinner);
        body.appendChild(loadContainer);
      }
    } catch (error) {
      console.error('❌ [LOAD-MANAGER] Error adding loader:', error);
      throw error;
    }
  }

  static removeLoader() {
    try {
      const loadContainer = document.getElementById('load-container');
      if(loadContainer) {
        loadContainer.remove();
      }
    } catch (error) {
      console.error('❌ [LOAD-MANAGER] Error removing loader:', error);
      throw error;
    }
  }
}



/**
 * Utility class for handling checkout link updates when postal code changes
 */
export class CheckoutLinkUpdater {
  /**
   * Updates all checkout links with the new postal code
   * @param {string} postalCode - The new postal code
   * @param {HTMLElement|Document} [scope] - Optional scope element to limit search (defaults to document)
   */
  static updateCheckoutLinks(postalCode, scope = document) {
    try {
      const newCheckoutUrl = `/checkout?checkout[shipping_address][zip]=${postalCode}`;

      // Update data-href attributes (used in collection footer)
      const dataHrefElements = scope.querySelectorAll('[data-href*="checkout[shipping_address][zip]"]');
      dataHrefElements.forEach(element => {
        element.setAttribute('data-href', newCheckoutUrl);
      });

      // Update data-checkout-url attributes (used in cart drawer/summary)
      const dataCheckoutUrlElements = scope.querySelectorAll('[data-checkout-url*="checkout[shipping_address][zip]"]');
      dataCheckoutUrlElements.forEach(element => {
        element.setAttribute('data-checkout-url', newCheckoutUrl);
      });

    } catch (error) {
      console.error('❌ [CHECKOUT-LINKS] Error updating checkout links:', error);
    }
  }
}

// Make CheckoutLinkUpdater available globally to avoid import issues
/** @type {any} */ (window).CheckoutLinkUpdater = CheckoutLinkUpdater;